import React, {useCallback, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Dialog, DialogFooter, DialogHeader} from '@pb/kit-component-dialog-v2';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import Typography from '@pb/kit-component-typography';

import {IDP_PROVIDERS} from '../../constants';
import {clearSsoSessionStorage, useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {SUPPORT_EMAIL} from '../../utils/build-support-mailto';
import {INITIAL_DOMAIN_DATA, getSelectedDomains} from '../../utils/domain-validation';
import {StepFooter} from '../step-footer/step-footer';

import {
    reviewContainer,
    scimNote,
    statusSuccess,
    summaryCard,
    summaryHeading,
    summaryLabel,
    summaryRow,
    summaryValue,
    toggleCard,
    toggleDescription,
    toggleRow,
} from './review-and-enable.module.styl';

import type {VerifyDomainData} from '../../utils/domain-validation';

type ReviewAndEnableProps = {
    onContinue: () => unknown,
    onBack: () => void,
    saving?: boolean,
};

type TestEndUserData = {
    confirmed: boolean,
};

type ReviewData = {
    isSsoMandatory: boolean,
    saved: boolean,
};

const INITIAL_REVIEW_DATA: ReviewData = {
    isSsoMandatory: true,
    saved: false,
};

function getCheckedValue(payload: unknown): boolean {
    if (typeof payload === 'boolean') {
        return payload;
    }
    return Boolean((payload as {event?: {target?: {checked?: boolean}}})?.event?.target?.checked);
}

export const ReviewAndEnable: React.FC<ReviewAndEnableProps> = ({onContinue, onBack, saving}) => {
    const [selectedProvider] = useSsoSessionStorage<string | null>('selectedProvider', null);
    const [domainData] = useSsoSessionStorage<VerifyDomainData>('verifyDomain', INITIAL_DOMAIN_DATA);
    const [testEndUser] = useSsoSessionStorage<TestEndUserData>('testEndUser', {confirmed: false});
    const [reviewData, setReviewData] = useSsoSessionStorage<ReviewData>('reviewAndEnable', INITIAL_REVIEW_DATA);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const {requiredCompanyLoginToggle} = useEventTracking();

    const providerName = IDP_PROVIDERS.find(p => p.id === selectedProvider)?.name
        ?? IDP_PROVIDERS.find(p => p.id === 'other')?.name
        ?? 'Other Provider';
    const selectedDomains = getSelectedDomains(domainData.domains);
    const domainsText = selectedDomains.length > 0
        ? selectedDomains.map(d => d.value).join(', ')
        : '—';
    const trackedDomains = selectedDomains.map(d => d.value).join(', ');
    const allDomainsVerified = selectedDomains.length > 0
        && selectedDomains.every(d => d.verified);
    const domainsAtText = selectedDomains.map(d => `@${d.value}`).join(', ');
    const endUserTested = testEndUser.confirmed;

    const handleToggle = useCallback((payload: unknown) => {
        const checked = getCheckedValue(payload);
        requiredCompanyLoginToggle({
            action: checked ? 'enable' : 'disable',
            domain: trackedDomains,
            idpProvider: selectedProvider ?? 'unknown',
        });
        setReviewData({...reviewData, isSsoMandatory: checked, saved: false});
    }, [requiredCompanyLoginToggle, reviewData, selectedProvider, setReviewData, trackedDomains]);

    const handleFinishClick = useCallback(() => {
        setShowConfirmDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setShowConfirmDialog(false);
    }, []);

    const handleConfirm = useCallback(async () => {
        setShowConfirmDialog(false);
        const success = await onContinue();
        if (success === true) {
            clearSsoSessionStorage();
        }
    }, [onContinue]);

    return (
        <Group direction="v" gap="l" className={reviewContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    Review &amp; Enable Company SSO system
                </Typography>
                <Typography variant="body-level-1">
                    Review your configuration and enable Company SSO system for your organization.
                </Typography>
            </Group>

            <div className={summaryCard}>
                <Typography variant="heading-level-3" className={summaryHeading}>
                    Configuration Summary
                </Typography>

                <div className={summaryRow}>
                    <span className={summaryLabel}>Login Provider</span>
                    <span className={summaryValue}>{providerName}</span>
                </div>
                <div className={summaryRow}>
                    <span className={summaryLabel}>Domains</span>
                    <span className={summaryValue}>{domainsText}</span>
                </div>
                <div className={summaryRow}>
                    <span className={summaryLabel}>Domains Verified</span>
                    <span className={`${summaryValue} ${allDomainsVerified ? statusSuccess : ''}`}>
                        {allDomainsVerified ? (
                            <>
                                <Icon type={Icons.CHECK} size="Size_14"/>
                                Yes
                            </>
                        ) : 'No'}
                    </span>
                </div>
                <div className={summaryRow}>
                    <span className={summaryLabel}>Metadata Uploaded</span>
                    <span className={`${summaryValue} ${statusSuccess}`}>
                        <Icon type={Icons.CHECK} size="Size_14"/>
                        Yes
                    </span>
                </div>
                <div className={summaryRow}>
                    <span className={summaryLabel}>Test Login</span>
                    <span className={`${summaryValue} ${endUserTested ? statusSuccess : ''}`}>
                        {endUserTested ? (
                            <>
                                <Icon type={Icons.CHECK} size="Size_14"/>
                                Passed
                            </>
                        ) : 'Not completed'}
                    </span>
                </div>
            </div>

            <div className={toggleCard}>
                <div className={toggleRow}>
                    <Group direction="v" gap="xs">
                        <Group gap="s">
                            <CheckBox
                                checked={reviewData.isSsoMandatory}
                                onCheckBoxChange={handleToggle}
                                usePropsOnly={true}
                            />
                            <Typography variant="heading-level-3">
                                Require SSO Login
                            </Typography>
                        </Group>
                        <span className={toggleDescription}>
                            When enabled, all users with <strong>{domainsAtText || '...'}</strong> will
                            be required to sign in using your Company SSO system.
                            Password login will be disabled.
                        </span>
                    </Group>
                </div>
            </div>

            {reviewData.isSsoMandatory ? (
                <Message color="warning">
                    When enabled, all users with <strong>{domainsAtText}</strong> will
                    be required to sign in using your Company SSO system.
                    Password login will be disabled for these users. Make sure all users have
                    been assigned to the SSO application in your Identity Provider before enabling this setting.
                </Message>
            ) : (
                <Message color="error">
                    You must enable 'Require SSO Login' before completing SSO setup.
                </Message>
            )}

            <Typography variant="body-level-1" className={scimNote}>
                Interested in setting up SCIM? Please email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('SCIM setup')}`}>
                    {SUPPORT_EMAIL}
                </a>
                {' '}for more details.
            </Typography>

            <StepFooter
                onContinue={handleFinishClick}
                onBack={onBack}
                continueDisabled={saving || !reviewData.isSsoMandatory}
                continueLabel="Finish"
            />

            <Dialog
                shown={showConfirmDialog}
                setShown={visible => {
                    if (!visible) {
                        handleCloseDialog();
                    }
                }}>
                <DialogHeader onClose={handleCloseDialog}>
                    <Typography variant="heading-level-2">
                        Complete SSO Setup?
                    </Typography>
                </DialogHeader>
                <Group direction="v" gap="m" style={{padding: 24}}>
                    <Typography variant="body-level-1">
                        After completion, all SSO settings will be saved and you will no longer
                        be able to change them independently.
                        <br/>
                        Any further changes must be made through the PitchBook support team.
                    </Typography>
                </Group>
                <DialogFooter>
                    <Button variant="Type.SECONDARY" onClick={handleCloseDialog}>
                        No
                    </Button>
                    <Button variant="Type.PRIMARY" onClick={handleConfirm}>
                        Yes
                    </Button>
                </DialogFooter>
            </Dialog>
        </Group>
    );
};
